import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import BlacksmithWorkshop from '../components/BlacksmithWorkshop';
import ConnectWalletModal from '../components/ConnectWalletModal';
import { createICSpicyActor } from '../services/icspicy';
import { useAuth } from '../auth/useAuth';

interface Proposal {
  id: bigint;
  title: string;
  description: string;
  proposer: { [key: string]: any };
  created_at: bigint;
  voting_deadline: bigint;
  status: { [key: string]: any };
  votes_for: bigint;
  votes_against: bigint;
}

interface Vote {
  proposal_id: bigint;
  voter: { [key: string]: any };
  vote: { [key: string]: any };
  voting_power: bigint;
  timestamp: bigint;
}

export default function DAOPage() {
  const { agent, principal, isAuthenticated } = useAuth();
  const [showConnectModal, setShowConnectModal] = useState(false);
  const [proposals, setProposals] = useState<Proposal[]>([]);
  const [votingPower, setVotingPower] = useState<bigint>(BigInt(0));
  const [userVotes, setUserVotes] = useState<Map<bigint, Vote>>(new Map());
  const [loading, setLoading] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState<string | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showVoteModal, setShowVoteModal] = useState<bigint | null>(null);
  const [newProposal, setNewProposal] = useState({ title: '', description: '', deadline: '' });
  const [creating, setCreating] = useState(false);
  const [voting, setVoting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    if (agent && principal) {
      loadProposals();
      loadVotingPower();
      loadUserVotes();
    }
  }, [agent, principal]);

  const loadProposals = async () => {
    if (!agent) return;
    setLoading(true);
    try {
      const actor = await createICSpicyActor(agent);
      const status = selectedStatus ? { [selectedStatus]: null } : null;
      const props = await actor.get_proposals(status);
      setProposals(props);
    } catch (err) {
      console.error('Failed to load proposals:', err);
      setError('Failed to load proposals');
    } finally {
      setLoading(false);
    }
  };

  const loadVotingPower = async () => {
    if (!agent || !principal) return;
    try {
      const actor = await createICSpicyActor(agent);
      const power = await actor.get_voting_power(principal);
      setVotingPower(power);
    } catch (err) {
      console.error('Failed to load voting power:', err);
    }
  };

  const loadUserVotes = async () => {
    if (!agent || !principal) return;
    try {
      const actor = await createICSpicyActor(agent);
      const votes = await actor.get_user_votes(principal);
      const voteMap = new Map<bigint, Vote>();
      votes.forEach(vote => {
        voteMap.set(vote.proposal_id, vote);
      });
      setUserVotes(voteMap);
    } catch (err) {
      console.error('Failed to load user votes:', err);
    }
  };

  useEffect(() => {
    loadProposals();
  }, [selectedStatus]);

  const handleCreateProposal = async () => {
    if (!agent) return;
    if (!newProposal.title || !newProposal.description || !newProposal.deadline) {
      setError('Please fill in all fields');
      return;
    }

    const deadline = BigInt(new Date(newProposal.deadline).getTime() * 1_000_000);
    setCreating(true);
    setError(null);
    try {
      const actor = await createICSpicyActor(agent);
      await actor.create_proposal(newProposal.title, newProposal.description, deadline);
      setSuccess('Proposal created successfully');
      setShowCreateModal(false);
      setNewProposal({ title: '', description: '', deadline: '' });
      loadProposals();
    } catch (err: any) {
      setError(err.message || 'Failed to create proposal');
    } finally {
      setCreating(false);
    }
  };

  const handleVote = async (proposalId: bigint, voteChoice: 'For' | 'Against') => {
    if (!agent) return;
    setVoting(true);
    setError(null);
    try {
      const actor = await createICSpicyActor(agent);
      const vote = voteChoice === 'For' ? { For: null } : { Against: null };
      await actor.vote_on_proposal(proposalId, vote);
      setSuccess('Vote recorded successfully');
      setShowVoteModal(null);
      loadProposals();
      loadUserVotes();
    } catch (err: any) {
      setError(err.message || 'Failed to vote');
    } finally {
      setVoting(false);
    }
  };

  const formatDate = (timestamp: bigint) => {
    const date = new Date(Number(timestamp) / 1_000_000);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
  };

  const getStatusLabel = (status: { [key: string]: any }) => {
    if (status.Active) return 'Active';
    if (status.Passed) return 'Passed';
    if (status.Rejected) return 'Rejected';
    if (status.Expired) return 'Expired';
    return 'Unknown';
  };

  const getStatusColor = (status: { [key: string]: any }) => {
    if (status.Active) return 'bg-green-900/50 text-green-300';
    if (status.Passed) return 'bg-blue-900/50 text-blue-300';
    if (status.Rejected) return 'bg-red-900/50 text-red-300';
    if (status.Expired) return 'bg-gray-700 text-gray-300';
    return 'bg-gray-700 text-gray-300';
  };

  const hasVoted = (proposalId: bigint) => {
    return userVotes.has(proposalId);
  };

  const getUserVote = (proposalId: bigint) => {
    const vote = userVotes.get(proposalId);
    if (!vote) return null;
    return vote.vote.For ? 'For' : 'Against';
  };

  if (!isAuthenticated) {
    return (
      <BlacksmithWorkshop>
        <div className="container mx-auto px-4 py-8 text-center">
          <h1 className="text-4xl font-bold mb-4 text-gold">IC SPICY DAO</h1>
          <p className="text-gray-300 mb-6">Connect your wallet to participate in governance</p>
          <button
            onClick={() => setShowConnectModal(true)}
            className="px-6 py-3 bg-gold text-black rounded-lg font-semibold hover:bg-yellow-500 transition"
          >
            Connect Wallet
          </button>
          {showConnectModal && (
            <ConnectWalletModal onClose={() => setShowConnectModal(false)} />
          )}
        </div>
      </BlacksmithWorkshop>
    );
  }

  return (
    <BlacksmithWorkshop>
      <div className="container mx-auto px-4 py-8">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-8"
        >
          <h1 className="text-5xl font-bold mb-4 bg-gradient-to-r from-gold via-yellow-500 to-gold bg-clip-text text-transparent">
            IC SPICY DAO
          </h1>
          <p className="text-xl text-gray-300">Decentralized Governance for NFT Holders</p>
          <div className="mt-4 text-lg text-gold">
            Your Voting Power: {votingPower.toString()} ICP NFT{votingPower !== BigInt(1) ? 's' : ''}
          </div>
        </motion.div>

        {/* Error/Success Messages */}
        {error && (
          <div className="mb-4 p-4 bg-red-900/50 border border-red-500 rounded-lg text-red-200">
            {error}
          </div>
        )}
        {success && (
          <div className="mb-4 p-4 bg-green-900/50 border border-green-500 rounded-lg text-green-200">
            {success}
          </div>
        )}

        {/* Actions */}
        <div className="flex flex-wrap gap-4 mb-6 justify-center">
          <button
            onClick={() => setSelectedStatus(null)}
            className={`px-4 py-2 rounded-lg ${
              selectedStatus === null
                ? 'bg-gold text-black'
                : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
            }`}
          >
            All
          </button>
          <button
            onClick={() => setSelectedStatus('Active')}
            className={`px-4 py-2 rounded-lg ${
              selectedStatus === 'Active'
                ? 'bg-gold text-black'
                : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
            }`}
          >
            Active
          </button>
          <button
            onClick={() => setSelectedStatus('Passed')}
            className={`px-4 py-2 rounded-lg ${
              selectedStatus === 'Passed'
                ? 'bg-gold text-black'
                : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
            }`}
          >
            Passed
          </button>
          <button
            onClick={() => setSelectedStatus('Rejected')}
            className={`px-4 py-2 rounded-lg ${
              selectedStatus === 'Rejected'
                ? 'bg-gold text-black'
                : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
            }`}
          >
            Rejected
          </button>
          {votingPower > BigInt(0) && (
            <button
              onClick={() => setShowCreateModal(true)}
              className="px-4 py-2 bg-gold text-black rounded-lg font-semibold hover:bg-yellow-500"
            >
              Create Proposal
            </button>
          )}
        </div>

        {/* Proposals List */}
        {loading ? (
          <div className="text-center py-12 text-gray-400">Loading proposals...</div>
        ) : proposals.length === 0 ? (
          <div className="text-center py-12 text-gray-400">No proposals found</div>
        ) : (
          <div className="space-y-4">
            {proposals.map(proposal => (
              <motion.div
                key={proposal.id.toString()}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="glass-dark rounded-2xl p-6"
              >
                <div className="flex justify-between items-start mb-4">
                  <div className="flex-1">
                    <h3 className="text-2xl font-bold text-gold mb-2">{proposal.title}</h3>
                    <p className="text-gray-300 mb-4">{proposal.description}</p>
                  </div>
                  <span
                    className={`px-3 py-1 rounded ${getStatusColor(proposal.status)}`}
                  >
                    {getStatusLabel(proposal.status)}
                  </span>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4 text-sm">
                  <div>
                    <div className="text-gray-400">Votes For</div>
                    <div className="text-xl font-bold text-green-400">
                      {proposal.votes_for.toString()}
                    </div>
                  </div>
                  <div>
                    <div className="text-gray-400">Votes Against</div>
                    <div className="text-xl font-bold text-red-400">
                      {proposal.votes_against.toString()}
                    </div>
                  </div>
                  <div>
                    <div className="text-gray-400">Created</div>
                    <div className="text-gray-300">{formatDate(proposal.created_at)}</div>
                  </div>
                  <div>
                    <div className="text-gray-400">Deadline</div>
                    <div className="text-gray-300">{formatDate(proposal.voting_deadline)}</div>
                  </div>
                </div>

                {proposal.status.Active && votingPower > BigInt(0) && (
                  <div className="flex gap-4">
                    {hasVoted(proposal.id) ? (
                      <div className="px-4 py-2 bg-gray-700 rounded-lg text-gray-300">
                        You voted: {getUserVote(proposal.id)}
                      </div>
                    ) : (
                      <>
                        <button
                          onClick={() => setShowVoteModal(proposal.id)}
                          className="px-6 py-2 bg-gold text-black rounded-lg font-semibold hover:bg-yellow-500"
                        >
                          Vote
                        </button>
                      </>
                    )}
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        )}

        {/* Create Proposal Modal */}
        {showCreateModal && (
          <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="glass-dark rounded-2xl p-6 w-full max-w-2xl"
            >
              <h3 className="text-2xl font-bold text-gold mb-4">Create Proposal</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-gray-300 mb-2">Title</label>
                  <input
                    type="text"
                    value={newProposal.title}
                    onChange={e => setNewProposal({ ...newProposal, title: e.target.value })}
                    className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white"
                  />
                </div>
                <div>
                  <label className="block text-gray-300 mb-2">Description</label>
                  <textarea
                    value={newProposal.description}
                    onChange={e => setNewProposal({ ...newProposal, description: e.target.value })}
                    className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white"
                    rows={5}
                  />
                </div>
                <div>
                  <label className="block text-gray-300 mb-2">Voting Deadline</label>
                  <input
                    type="datetime-local"
                    value={newProposal.deadline}
                    onChange={e => setNewProposal({ ...newProposal, deadline: e.target.value })}
                    className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white"
                  />
                </div>
                <div className="flex gap-4">
                  <button
                    onClick={() => setShowCreateModal(false)}
                    className="flex-1 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleCreateProposal}
                    disabled={creating}
                    className="flex-1 py-2 bg-gold text-black rounded-lg font-semibold hover:bg-yellow-500 disabled:opacity-50"
                  >
                    {creating ? 'Creating...' : 'Create Proposal'}
                  </button>
                </div>
              </div>
            </motion.div>
          </div>
        )}

        {/* Vote Modal */}
        {showVoteModal && (
          <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="glass-dark rounded-2xl p-6 w-full max-w-md"
            >
              <h3 className="text-2xl font-bold text-gold mb-4">Cast Your Vote</h3>
              <div className="mb-4 text-gray-300">
                Voting Power: {votingPower.toString()} ICP NFT{votingPower !== BigInt(1) ? 's' : ''}
              </div>
              <div className="flex gap-4">
                <button
                  onClick={() => handleVote(showVoteModal, 'For')}
                  disabled={voting}
                  className="flex-1 py-3 bg-green-900/50 text-green-300 rounded-lg font-semibold hover:bg-green-800/50 disabled:opacity-50"
                >
                  Vote For
                </button>
                <button
                  onClick={() => handleVote(showVoteModal, 'Against')}
                  disabled={voting}
                  className="flex-1 py-3 bg-red-900/50 text-red-300 rounded-lg font-semibold hover:bg-red-800/50 disabled:opacity-50"
                >
                  Vote Against
                </button>
              </div>
              <button
                onClick={() => setShowVoteModal(null)}
                className="w-full mt-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600"
              >
                Cancel
              </button>
            </motion.div>
          </div>
        )}

        {showConnectModal && (
          <ConnectWalletModal onClose={() => setShowConnectModal(false)} />
        )}
      </div>
    </BlacksmithWorkshop>
  );
}

